import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Table, TableWrapper } from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { auditActionLabel, auditReasonLabel } from "@/lib/audit-action-labels";
import { fetchAuditEvents, type AdminAuditEvent } from "@/lib/api";
import { usePermission } from "@/hooks/use-permission";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { legacyErrorReason } from "@/lib/api-failure";
import { hasPermission, roleDisplayLabel, type AppRole } from "@/lib/permissions";

/* USR-06 — Admin Audit Trail (API-backed).
   Canonical source: Figma IAM-09 / prototype/pages/admin/audit-log.html.
   Reads real auth audit events from `GET /v1/admin/audit` (org-scoped,
   paginated, filterable). Search and time-range are server-side filters;
   action/actor facets are intentionally out of scope for this pass (the API
   accepts `actor`/`action`/`from`/`to`, the UI exposes search + time-range +
   pagination). IP is not shown — `auth_audit_events` does not persist source
   IP in its base columns. Result badge is derived from `outcome` (ALLOW /
   DENY / ERROR) with the `reasonCode` shown alongside. UX audit D08
   (ALL-5825): the Action cell and that reason text now render the human
   labels from `lib/audit-action-labels` ("Signed in" instead of
   BROWSER_LOGIN_ALLOWED), because the stored values are machine keys, not
   sentences. The stored value is kept verbatim in the cell's `title`, so no
   evidence is lost and a reviewer can still read exactly what the backend
   recorded. Target/Actor
   cells linkify any real USR-/TXI-/WLT-/SVC- id they contain to the actual
   existing route for that record — a direct, non-invented reuse of ids that
   already exist elsewhere in this app. "Export CSV" is handlerless (no gateway
   route fronts an audit-evidence export — ALL-5219) and renders only for a
   session that holds `evidence:export`. No loaded API contract grants it
   (`src/lib/permissions.ts` maps the key to no backend permission value), so
   every real viewer sees no control at all rather than a disabled one — the
   lab's affordance check (ALL-5830) requires its absence, since a disabled
   button is still announced to a screen reader as an action the reader cannot
   use. */

const ID_PATTERN = /\b(USR|TXI|WLT|SVC)-\d+\b/g;

/**
 * Service-account actor prefix. Platform-to-platform calls are audited under a
 * machine identity (`sa_gateway_to_auth_service`, `sa_…`), never a human.
 */
const SERVICE_ACTOR_PREFIX = "sa_";

/**
 * The single definition of "service activity" for this page (UX audit D08).
 *
 * On dev the first dozens of audit rows are machine-to-machine traffic —
 * actor `sa_gateway_to_auth_service`, action `API_KEY_VERIFICATION_ALLOWED`,
 * no target — so a "who did what" demo opens on rows that name no person.
 * A row is service activity when its actor is a service account (`sa_…`) or
 * its action is an API-key verification, which only a machine principal emits.
 *
 * Exported so the page and its tests agree on one rule rather than each
 * re-deriving it; the toggle in the UI hides exactly these rows.
 */
export function isServiceActivity(
  event: Pick<AdminAuditEvent, "actor" | "action">,
): boolean {
  const actor = (event.actor ?? "").trim();
  const action = (event.action ?? "").trim();
  return actor.startsWith(SERVICE_ACTOR_PREFIX) || action.endsWith("_VERIFICATION_ALLOWED");
}

function LinkifiedText({ text }: { text: string | null | undefined }) {
  // `actor`/`target` are legitimately null on real auth_audit_events rows —
  // e.g. API_KEY_VERIFICATION_ALLOWED events have no target, and some
  // system-originated events (BROWSER_LOGIN_ALLOWED) have no actor. Verified
  // live against dev.alloy.build (ALL-4729 G6): 3 of 5 real rows in one
  // GET /v1/admin/audit response had target: null. Render an em dash rather
  // than crash the whole table on `.slice`/`.length` of null (ALL-4729 G6).
  if (!text) return <>—</>;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(ID_PATTERN);
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const id = match[0];
    const prefix = match[1];
    const href = prefix === "USR" ? `/admin/users/${id}` : prefix === "TXI" ? `/transfers/${id}` : prefix === "WLT" ? `/wallets/${id}` : "/admin/service-accounts";
    parts.push(
      <Link key={`${id}-${key++}`} to={href} className="font-semibold text-primary hover:underline">
        {id}
      </Link>
    );
    lastIndex = match.index + id.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

/**
 * A timestamp cell. `toLocaleString()` alone renders a different instant for
 * every reader's timezone with nothing to anchor it, so the human-readable
 * locale string is wrapped in a `<time>` element whose `dateTime`/`title` carry
 * the exact ISO instant — a screen reader reads the machine value and a hover
 * shows it, while sighted readers keep the local rendering. An unparsable value
 * falls back to the raw string, still inside the wrapper, and is never
 * reformatted into a date the backend did not send.
 */
function TimestampCell({ iso }: { iso: string }) {
  const d = new Date(iso);
  const valid = !Number.isNaN(d.getTime());
  const label = valid ? d.toLocaleString() : iso;
  const exact = valid ? d.toISOString() : iso;
  return (
    <time dateTime={exact} title={exact}>
      {label}
    </time>
  );
}

function outcomeLabel(outcome: string): string {
  const o = (outcome || "").trim();
  if (!o) return "—";
  return o.charAt(0).toUpperCase() + o.slice(1).toLowerCase();
}

function outcomeTone(outcome: string): "success" | "warning" | "danger" | "info" {
  const o = (outcome || "").trim().toUpperCase();
  if (o === "ALLOW") return "success";
  if (o === "DENY") return "danger";
  if (o === "ERROR") return "warning";
  return "info";
}

/** The server-side filters the current request actually carried. */
interface AuditFilters {
  query: string;
  timeRange: string;
}

function hasActiveFilters(filters: AuditFilters): boolean {
  return filters.query.trim().length > 0 || filters.timeRange !== "all";
}

/** Human-readable summary of `AuditFilters`, for the empty state. */
function auditFilterSummary(filters: AuditFilters): string {
  const time = filters.timeRange === "24h" ? "Last 24h" : "All time";
  const query = filters.query.trim();
  return query ? `${time} · search "${query}"` : time;
}

/**
 * Empty state for the audit table. A zero-row response has two different
 * causes and they call for different user actions, so they get different
 * copy: nothing happened in the selected period, or the filters excluded
 * everything that did. The active-filter summary is always rendered so a
 * reader can see which period the result describes — and it is the only
 * input to this branch, so two identical responses render differently only
 * when the user applied a filter. Replaces the previous single hard-coded
 * line, which blamed filters that were not set. (A shared
 * `components/data-display/table-state.tsx` does not exist in this
 * checkout, so the fix stays local to this feature.)
 */
function AuditEmptyState({ filters }: { filters: AuditFilters }) {
  const filtered = hasActiveFilters(filters);
  return (
    <div
      className="px-4 py-10 text-center"
      data-testid="audit-empty"
      data-empty-kind={filtered ? "filtered" : "none"}
    >
      <p className="text-sm text-muted">
        {filtered ? "No audit events match your filters." : "No audit events in this period."}
      </p>
      <p className="mt-1 text-[12px] text-subtle">Filters: {auditFilterSummary(filters)}</p>
      {filtered && (
        <p className="mt-1 text-[12px] text-subtle">
          Clear the search or widen the time range to see events from this period.
        </p>
      )}
    </div>
  );
}

/**
 * The roles that grant `audit-log:read`, named in the denial.
 *
 * Derived from the permission table in `lib/permissions.ts` (its header calls
 * itself the single source of truth for RBAC) rather than hand-listed, so a
 * role gaining or losing the permission moves this copy with it. The generic
 * organization admin is excluded by construction — the sentence's second half
 * already names it, and a viewer reading a denial by definition does not hold
 * it. Labels come from `roleDisplayLabel`, so the reader sees "Auditor", never
 * the raw `auditor` id a role assignment screen would use.
 */
const AUDIT_ROLE_KEYS: AppRole[] = [
  "security-admin",
  "developer",
  "wallet-admin",
  "wallet_operator",
  "compliance-officer",
  "policy_approver",
  "treasury-operator",
  "finance_operator",
  "finance_viewer",
  "auditor",
  "support-readonly",
];

function rolesWithPermission(permission: "audit-log:read"): string[] {
  return AUDIT_ROLE_KEYS.filter((role) => hasPermission(role, permission)).map((role) => roleDisplayLabel(role));
}

/** "A", "A or B", "A, B or C" — a list of role names read as alternatives. */
function roleAlternatives(labels: string[]): string {
  if (labels.length === 0) return "Organization Admin";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

export function AdminAuditTrail() {
  const plainError = usePlainErrorMessage();
  const canRead = usePermission("audit-log:read");
  const canExport = usePermission("evidence:export");
  const [query, setQuery] = useState("");
  const [timeRange, setTimeRange] = useState("all");
  const [page, setPage] = useState(1);
  // UX audit D08 — service traffic buries the human rows. Default hides it,
  // but only when the loaded page also carries a human row (see below).
  const [showService, setShowService] = useState(false);
  const [events, setEvents] = useState<AdminAuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorSupport, setErrorSupport] = useState<{ supportReference?: string; capability?: string }>({});
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const handle = setTimeout(() => {
      const from = timeRange === "24h" ? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() : undefined;
      fetchAuditEvents({
        query: query.trim() || undefined,
        from,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }).then((r) => {
        if (cancelled) return;
        setEvents(r.events);
        setTotal(r.total);
        setError(r.error ?? null);
        setErrorSupport({ supportReference: r.supportReference, capability: r.capability });
        setLoading(false);
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, timeRange, page, canRead]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const denied = error === "HTTP 401" || error === "HTTP 403";

  // UX audit D08. Service rows are hidden by default, but ONLY when the
  // loaded page also holds at least one human row: a page that is entirely
  // service activity is shown in full (an empty table caused by the toggle is
  // worse than the noise it removes) and labelled as such. No extra fetch is
  // issued — the decision is made over the rows already on this page, and the
  // server-side search/time-range filters and pagination are untouched.
  const serviceCount = events.filter(isServiceActivity).length;
  const hasServiceRows = serviceCount > 0;
  const hasHumanRows = events.length > serviceCount;
  const hideService = hasServiceRows && hasHumanRows && !showService;
  const visibleEvents = hideService ? events.filter((e) => !isServiceActivity(e)) : events;

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Audit Trail</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Admin Audit Trail</h1>
        <p className="mt-1 text-sm text-muted">Every administrative action across Identity &amp; Access, service accounts, and organization settings.</p>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search actors, actions, targets..."
            /* The placeholder is not an accessible name — a screen reader
               announces this field as an unlabelled edit box, and the hint
               disappears as soon as the user types. The name repeats the
               visible placeholder text verbatim so it stays consistent with
               what a sighted user reads. */
            aria-label="Search actors, actions, targets"
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        <Select value={timeRange} onChange={(e) => { setTimeRange(e.target.value); setPage(1); }} aria-label="Filter by time range" className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted">
          <option value="all">All Time</option>
          <option value="24h">Last 24h</option>
        </Select>
        {canExport && (
          <Button variant="secondary" className="ml-auto rounded-[8px]">Export CSV</Button>
        )}
      </div>

      {!canRead && (
        <div
          data-testid="audit-denied"
          className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted"
        >
          {/* Name what the viewer needs, the way features/wallets/wallet-request.tsx
              does — a generic "you don't have permission" is not actionable. ADM-04. */}
          Viewing the audit trail needs the {roleAlternatives(rolesWithPermission("audit-log:read"))} role. Ask an
          organization admin to assign it.
        </div>
      )}

      {canRead && denied && (
        <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
          You don&apos;t have permission to view audit logs for this organization.
          <SupportDetails reference={errorSupport.supportReference} capability={errorSupport.capability} />
        </div>
      )}

      {canRead && !denied && error && error !== "HTTP 401" && error !== "HTTP 403" && (
        /* The machine line ("Code: HTTP_500") is gone: the status is an internal
           detail a reader cannot act on, and the same failure is already stated
           in plain language by `plainError`. When the gateway returned one, the
           support reference is rendered through the shared SupportDetails /
           CopyableReference component instead — the string support can actually
           act on. (ALL-5774/D636, matching admin-console-failure.tsx.) */
        <div role="alert" className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
          Couldn&apos;t load audit events. {plainError({ reasonCode: legacyErrorReason(error) })}
          <SupportDetails reference={errorSupport.supportReference} />
        </div>
      )}

      {canRead && !denied && loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading audit events...</div>
      )}

      {canRead && !denied && !loading && !error && events.length === 0 && (
        <TableWrapper>
          <AuditEmptyState filters={{ query, timeRange }} />
        </TableWrapper>
      )}

      {canRead && !denied && !loading && !error && events.length > 0 && (
        <TableWrapper>
          {hasServiceRows && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-bg px-4 py-[9px] text-[12px] text-muted">
              {hasHumanRows ? (
                <>
                  <label className="flex cursor-pointer items-center gap-2 text-ink">
                    <input
                      type="checkbox"
                      data-testid="audit-show-service"
                      checked={showService}
                      onChange={(e) => setShowService(e.target.checked)}
                      className="size-3.5 accent-[var(--ads-primary)]"
                    />
                    Show service activity
                  </label>
                  {hideService && (
                    <span data-testid="audit-service-hidden-count">
                      {serviceCount} service event{serviceCount === 1 ? "" : "s"} hidden on this page
                    </span>
                  )}
                </>
              ) : (
                <span data-testid="audit-service-only">Only service activity in this period</span>
              )}
            </div>
          )}
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg">
                <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Timestamp</th>
                <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Actor</th>
                <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Action</th>
                <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Target</th>
                <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Result</th>
              </tr>
            </thead>
            <tbody>
              {visibleEvents.map((e) => (
                <tr key={e.eventId} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink"><TimestampCell iso={e.occurredAt} /></td>
                  <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink"><LinkifiedText text={e.actor} /></td>
                  {/* UX audit D08: the stored action is a machine key; the cell
                      shows its human label and keeps the key in `title`. */}
                  <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink" title={e.action}>
                    {auditActionLabel(e.action)}
                  </td>
                  <td className="px-4 py-[9px] text-[11.5px] text-ink"><LinkifiedText text={e.target} /></td>
                  <td className="px-4 py-[9px] whitespace-nowrap">
                    <Badge variant={outcomeTone(e.outcome)} size="sm">{outcomeLabel(e.outcome)}</Badge>
                    {e.reasonCode && e.reasonCode.toUpperCase() !== (e.outcome || "").toUpperCase() && (
                      // Same rule as before — the reason shows only when it adds
                      // something the badge does not — but as prose, with the
                      // stored code in `title` (UX audit D08).
                      <span className="ml-1.5 text-[10px] text-muted" title={e.reasonCode}>
                        {auditReasonLabel(e.reasonCode)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} totalItems={total} pageSize={PAGE_SIZE} />
        </TableWrapper>
      )}
    </div>
  );
}
